/**
 * Auth return-URL plumbing.
 *
 * The app has three logout paths — explicit "Log Out" button, an API
 * 401 that hard-reloads to `/login`, and the ProtectedRoute guard that
 * bounces unauthenticated users. All three used to discard the URL the
 * user was on, so login always dropped them back on Home. This helper
 * centralises capturing and restoring that URL.
 *
 * We deliberately use `sessionStorage` (not `localStorage`) because:
 *   • Tab-scoped is the right lifetime — a return-URL from tab A
 *     shouldn't rehydrate in tab B.
 *   • It survives the `window.location.href = '/login'` full-page
 *     reload that the 401 handler triggers, which is the whole reason
 *     router `state.from` alone isn't enough.
 *
 * Router `location.state.from` is *also* set at the SPA logout sites
 * so the redirect works when React state survives across the
 * navigation (no reload). Consumers prefer `state.from` first, then
 * fall back to `sessionStorage`.
 */

const KEY = 'auth_return_to'

/**
 * Capture a path (pathname + search + hash, ideally) to restore after
 * the next successful login. Silently no-ops for unsafe values so the
 * caller doesn't have to sanity-check.
 */
export function setAuthReturnTo(path: string | null | undefined): void {
  if (!isSafeReturnPath(path)) return
  try {
    sessionStorage.setItem(KEY, path as string)
  } catch {
    /* private mode / storage disabled — just skip */
  }
}

/**
 * Read and clear the stored return path. Returns `null` when nothing
 * was captured, the storage isn't accessible, or the value fails
 * sanitation (paranoia — sessionStorage is same-origin but we still
 * defend against a stale value from a code change).
 */
export function consumeAuthReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    return isSafeReturnPath(raw) ? raw : null
  } catch {
    return null
  }
}

/**
 * Build a path string that captures where the user is right now.
 * Prefer this over passing bare `location.pathname`, which drops
 * query strings the target page may depend on (report filters,
 * search terms, etc.).
 */
export function currentPathForReturn(loc: {
  pathname: string
  search?: string
  hash?: string
}): string {
  return `${loc.pathname}${loc.search ?? ''}${loc.hash ?? ''}`
}

/**
 * Path must be a same-origin relative URL and must not itself be the
 * login page — otherwise we'd redirect from `/login` to `/login`
 * after a successful sign-in.
 *
 * Rejects:
 *   • empty / null / non-string
 *   • absolute URLs (`http://…`, `//example.com/…`) — open-redirect
 *   • paths not starting with `/`
 *   • the `/login` page itself (any query/hash form)
 */
function isSafeReturnPath(path: unknown): path is string {
  if (typeof path !== 'string' || path.length === 0) return false
  if (!path.startsWith('/')) return false
  if (path.startsWith('//')) return false
  // Reject `/login`, `/login?…`, `/login#…`
  if (path === '/login' || path.startsWith('/login?') || path.startsWith('/login#')) {
    return false
  }
  return true
}
